import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { TextLink } from '../../common/components/typography';
import {
  CloneFormTableRowButton,
  CreateTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  FormTablePreviewOnlyField,
  TanstackFormTable,
  getFormTableFieldName,
  getValidPeriodDisplayValueWithFormValueAttached,
} from '../../common/form';
import { CREATE_PARAM, isCreateParam } from '../../common/queryParams/create';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { getDatePeriodString, sortPeriod } from '../../common/utils';
import { useSystemClassifierItem } from '../../configuration';
import { SystemClassifierItem } from '../../systemClassifier/SystemClassifierItem';
import { ValidationRuleValidDataType } from '../schema';
import translations from '../translations/ValidationRulesViewTable.translations.json';
import { DataObjectFormDataType } from '../types';
import { usePanelParams } from '../usePanelParams';
import { defaultValidationRule } from './defaultValidationRule';
import { ValidationTableWrapper, useValidationRuleOptions } from './utils';
import { ValidationErrorLevel } from './ValidationErrorLevel';
import { ValidationRuleEditPanel } from './ValidationRuleEditPanel';
import { ValidationStatus } from './ValidationStatus';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { TextEllipsis } from '../../common/components';

const { addTranslations, useTranslation } = i18nNsFactory(
  'validation-rules-edit-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<ValidationRuleValidDataType>();

export function ValidationRulesEditTable() {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext<DataObjectFormDataType>();
  const { dirtyFields } = useFormState<DataObjectFormDataType>();
  const { validationRules } = useWatch<DataObjectFormDataType>();
  const options = useValidationRuleOptions();

  const [{ validationRuleId, validationRuleIndex }, setPanelParams] =
    usePanelParams();

  const submit = useCallback(
    (rule: ValidationRuleValidDataType) => {
      if (validationRuleId) {
        const validationRules = getValues('validationRules');
        if (isCreateParam(validationRuleId)) {
          setValue('validationRules', validationRules.concat(rule), {
            shouldDirty: true,
          });
        } else {
          const index = validationRules.findIndex(
            ({ id }) => id === Number(validationRuleId),
          );
          if (index >= 0) {
            setValue(`validationRules.${index}`, rule, {
              shouldDirty: true,
            });
          }
        }
      } else if (!isNaN(Number(validationRuleIndex))) {
        setValue(`validationRules.${Number(validationRuleIndex)}`, rule, {
          shouldDirty: true,
        });
      }
      setPanelParams();
    },
    [
      setValue,
      getValues,
      setPanelParams,
      validationRuleId,
      validationRuleIndex,
    ],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: t('column.code.label'),
        cell: CodeCell,
        meta: { width: '25ch', className: 'sticky-left' },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('groupCode', {
        header: t('column.groupCode.label'),
        cell: FormTablePreviewOnlyField,
        meta: { width: '15ch' },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('errorLevelId', {
        header: t('column.errorLevelId.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => <ValidationErrorLevel itemId={value} />,
            }}
          />
        ),
        meta: { width: 120 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('condition', {
        header: t('column.condition.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => <code>{value}</code>,
            }}
          />
        ),
        meta: { width: '20%', minWidth: 150, maxWidth: 250 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('expressionLeft', {
        header: t('column.expressionLeft.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => <code>{value}</code>,
            }}
          />
        ),
        meta: { width: '25%', minWidth: 150, maxWidth: 350 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('expressionOperatorId', {
        header: t('column.expressionOperatorId.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => (
                <SystemClassifierItem
                  code="VALIDATION_RULE_OPERATOR"
                  itemId={value}
                />
              ),
            }}
          />
        ),
        meta: { width: 125 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('expressionRight', {
        header: t('column.expressionRight.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => <code>{value}</code>,
            }}
          />
        ),
        meta: { width: '25%', minWidth: 150, maxWidth: 350 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('tolerance', {
        header: t('column.tolerance.label'),
        cell: FormTablePreviewOnlyField,
        meta: { width: 120 },
      }),
      columnHelper.accessor('ignoreError', {
        header: t('column.ignoreError.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) =>
                t(`column.ignoreError.${value ? 'yes' : 'no'}`),
            }}
          />
        ),
        meta: { width: 120 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('ignoreReason', {
        header: t('column.ignoreReason.label'),
        cell: FormTablePreviewOnlyField,
        meta: { width: '15%', minWidth: 250 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('errorDescription', {
        header: t('column.errorDescription.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
            }}
          />
        ),
        meta: { width: '15%', minWidth: 330, maxWidth: 350 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('statusId', {
        header: t('column.statusId.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => <ValidationStatus itemId={value} />,
            }}
          />
        ),
        meta: { width: 125 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor(
        (rowData) =>
          getValidPeriodDisplayValueWithFormValueAttached(rowData.validPeriod),
        {
          id: 'validPeriod',
          header: t('column.validPeriod.label'),
          cell: (props) => (
            <FormField
              name={getFormTableFieldName(props)}
              size="small"
              preview={{
                previewOnly: true,
                render: (value) => (
                  <TextEllipsis>
                    {getDatePeriodString(value?.[0], value?.[1])}
                  </TextEllipsis>
                ),
              }}
            />
          ),
          meta: { width: 200 },
          sortingFn: (a, b) =>
            sortPeriod(a.original.validPeriod, b.original.validPeriod),
          filterFn: 'validPeriod',
        },
      ),
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            <CreateTableRowButton
              title={t('button.add-validation-rule')}
              onClick={() => setPanelParams({ validationRuleId: CREATE_PARAM })}
              defaultRowValues={defaultValidationRule()}
              autoFocusField="code"
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => {
          return (
            <Flex justify="center" gap="small">
              <CloneFormTableRowButton
                context={props}
                resetId
                autoFocusField="code"
              />
              <DeleteFormTableRowButton context={props} />
            </Flex>
          );
        },
        meta: {
          width: 80,
          className: 'sticky-right',
        },
      }),
    ],
    [t, setPanelParams],
  );

  const { activeStatusId, errorId } = useValidationDefaults();

  const editable = useMemo(() => {
    if (validationRuleId) {
      if (isCreateParam(validationRuleId)) {
        const { errorLevelId, expressionOperatorId, statusId, ...row } =
          defaultValidationRule();

        return {
          ...row,
          errorLevelId: errorId,
          statusId: activeStatusId,
        };
      } else {
        return getValues('validationRules').find(
          ({ id }) => id === Number(validationRuleId),
        );
      }
    } else if (validationRuleIndex) {
      return (
        validationRuleIndex &&
        getValues(`validationRules.${Number(validationRuleIndex)}`)
      );
    }
  }, [
    getValues,
    validationRuleId,
    validationRuleIndex,
    activeStatusId,
    errorId,
  ]);

  const ruleIndex = useMemo(
    () =>
      (validationRules || []).findIndex(
        ({ id }) => id === Number(validationRuleId),
      ),
    [validationRules, validationRuleId],
  );

  return (
    <ValidationTableWrapper>
      <TanstackFormTable
        defaultSort={[{ id: 'code', desc: false }]}
        name="validationRules"
        columns={columns}
        options={options}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
      {editable && (
        <ValidationRuleEditPanel
          onClose={() => setPanelParams()}
          onSubmit={submit}
          validationRule={editable}
          validationRuleIsDirty={
            !!dirtyFields.validationRules &&
            editable &&
            ruleIndex !== undefined &&
            dirtyFields.validationRules[ruleIndex] !== undefined
          }
        />
      )}
    </ValidationTableWrapper>
  );
}

function useValidationDefaults() {
  const activeStatusId = useSystemClassifierItem(
    'VALIDATION_RULE_STATUS',
    'ACTIVE',
  )?.id;
  const errorId = useSystemClassifierItem(
    'VALIDATION_ERROR_LEVEL',
    'ERROR',
  )?.id;

  return { activeStatusId, errorId };
}

const CodeCell: FC<CellContext<ValidationRuleValidDataType, any>> = (props) => {
  const validationRuleIndex = props.row.index;
  const validationRuleId = props.row.original.id;
  const params = validationRuleId
    ? { validationRuleId }
    : { validationRuleIndex };
  const [, setPanelParams] = usePanelParams();
  const href = useHrefWithQueryParams(params);

  return (
    <FormField
      name={getFormTableFieldName(props)}
      size="small"
      preview={{
        previewOnly: true,
        render: (value) => (
          <TextLink
            id={`validationRules-${props.row.index}-code`}
            href={href}
            onClick={suppressHref(() => setPanelParams(params))}
          >
            {value}
          </TextLink>
        ),
      }}
    />
  );
};
